import ExcelJS from 'exceljs';
import type { TOptions } from 'i18next';
import { calculateRoomTotal } from '@/entities/estimate';
import { computeEstimateTotals } from '@/entities/estimate/lib/computeTotals';
import type { EstimateTotalsResult } from '@/entities/estimate/lib/computeTotals';
import type { BOMTotalsResult } from '@/entities/material';
import { Room, Service, ProjectMeta, CalculatedEstimateItem, EstimateItem } from '@/shared/model';
import type { TypedAdjustment } from '@/entities/estimate/model/types';
import i18n from '@/shared/config/i18n/i18n';
import { roundTo } from '@/shared/lib/number';

/**
 * WR-02: Formats a TypedAdjustment as a display string for Excel cells.
 * Returns null for identity mul (×1) or missing adjustment.
 */
function formatAdjDisplay(adj: TypedAdjustment | undefined | null): string | null {
  if (!adj || (adj.op === 'mul' && adj.value === 1)) return null;
  return adj.op === 'add' ? `+${adj.value}` : adj.op === 'sub' ? `-${adj.value}` : `x${adj.value}`;
}

/**
 * generateExcelTotals — pure helper that returns the EstimateTotalsResult that Excel
 * cells will actually write. Exported so the parity test can assert "what Excel writes"
 * without rendering a full workbook.
 *
 * Both exportToExcel (production path) and cross-format-parity.test.ts (test path) call
 * the SAME function — no second computation, no divergence.
 *
 * PDF-03 CARDINAL RULE: same computeEstimateTotals call used by the calculator and PDF.
 */
export function generateExcelTotals(
  rooms: Room[],
  projectItems: EstimateItem[] = [],
  safetyMargin: number = 0,
  vatRate: number = 0.23,
  projectOverrides?: { floorArea?: number; wallArea?: number; perimeter?: number },
): EstimateTotalsResult {
  return computeEstimateTotals(rooms, projectItems, safetyMargin, vatRate, projectOverrides);
}

const DANGEROUS_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

function sanitizeCell(value: string | number | undefined): string | number | undefined {
  if (value === undefined || value === null) return value;
  if (typeof value === 'number') return value;
  const str = String(value);
  if (DANGEROUS_PREFIXES.includes(str.charAt(0))) {
    return "'" + str;
  }
  return str;
}

const formatUnit = (type: string): string => {
  switch (type) {
    case 'pcs': return i18n.t('common.unit_pcs', 'шт');
    case 'mp': return i18n.t('common.unit_mp', 'мп');
    case 'm2_wall': return i18n.t('common.unit_m2_wall', 'м² (Стены)');
    case 'm2_floor': return i18n.t('common.unit_m2_floor', 'м² (Пол)');
    case 'm2_ceiling': return i18n.t('common.unit_m2_ceiling', 'м² (Потолок)');
    case 'mp_slopes': return i18n.t('common.unit_mp_slopes', 'мп (Откосы)');
    case 'm2': return i18n.t('common.unit_m2', 'м²');
    case 'lm': return i18n.t('common.unit_lm', 'мп');
    default: return type;
  }
};

export const exportToExcel = async (
  rooms: Room[],
  _servicesDB: Service[],
  _projectTotal: number,
  currency: string = 'PLN',
  meta: ProjectMeta = {},
  projectItems: EstimateItem[] = [],
  safetyMargin: number = 0,
  materialTotals?: BOMTotalsResult,
  projectOverrides?: { floorArea?: number; wallArea?: number; perimeter?: number },
): Promise<boolean | void> => {
  const t = (key: string, fallback: string, options?: TOptions) => i18n.t(key, fallback, options);
  const cur = currency;

  // PDF-03 CARDINAL: compute totals once using the shared authority.
  // These pre-computed roundTo values replace formula cells (D*E and SUM) — see PATTERNS.md.
  // CR-01: pass safetyMargin from caller so Excel matches UI/PDF totals for non-zero margins.
  // TRUTH-02: pass projectOverrides so global items priced by project geometry match the screen.
  const totalsResult = generateExcelTotals(rooms, projectItems, safetyMargin, meta.vatRate ?? 0.23, projectOverrides);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(t('excel.sheet_name', 'Kosztorys') as string);

  // Enable gridlines on screen
  worksheet.views = [{ showGridLines: true }];

  // Column styles and widths
  worksheet.columns = [
    { key: 'lp', width: 6 },
    { key: 'work', width: 45 },
    { key: 'unit', width: 10 },
    { key: 'rate', width: 14 },
    { key: 'qty', width: 12 },
    { key: 'total', width: 16 },
    { key: 'notes', width: 30 }
  ];

  // Helper styles
  const thinBorder: Partial<ExcelJS.Borders> = {
    top: { style: 'thin', color: { argb: 'CBD5E1' } },
    left: { style: 'thin', color: { argb: 'CBD5E1' } },
    bottom: { style: 'thin', color: { argb: 'CBD5E1' } },
    right: { style: 'thin', color: { argb: 'CBD5E1' } }
  };

  // 1. Title
  const titleRow = worksheet.addRow([t('excel.title', 'KOSZTORYS PRAC REMONTOWYCH') as string]);
  titleRow.height = 30;
  worksheet.mergeCells(`A1:G1`);
  titleRow.getCell(1).font = { name: 'Arial', size: 16, bold: true, color: { argb: '0F172A' } };
  titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };

  let currentRowIdx = 2;

  // 2. Global / project-level items layer (skeleton for Phase 1; full details + calc in later phases)
  if (projectItems && projectItems.length > 0) {
    const globalHeader = worksheet.addRow([t('excel.global_section', 'ПОЗИЦИИ ГЛОБАЛЬНОГО СЛОЯ (ОБЩИЕ РАБОТЫ)') as string]);
    globalHeader.font = { bold: true, color: { argb: '334155' } };
    worksheet.mergeCells(`A${currentRowIdx}:G${currentRowIdx}`);
    currentRowIdx++;
    // TRUTH-03: render every projectItems row — no truncation, no overflow-notice branch.
    projectItems.forEach((it, idx) => {
      // IN-02: use pre-computed lineTotal from totalsResult instead of (it as any).cost
      const lineResult = totalsResult.lines.find((l) => l.itemId === it.id);
      worksheet.addRow([
        idx + 1,
        (it.nameKey ? (i18n.t(it.nameKey, it.name) as string) : it.name) || '—',
        it.unit || '',
        roundTo(it.priceLabor || 0, 2),
        roundTo(it.userQty || it.qty || 0, 2),
        roundTo(lineResult?.lineTotal ?? 0, 2),
        ''
      ]);
      currentRowIdx++;
    });
  }

  // 3. Metadata Block (shifted)
  const addMetaRow = (value: string) => {
    const r = worksheet.addRow([value]);
    r.height = 18;
    worksheet.mergeCells(`A${currentRowIdx}:G${currentRowIdx}`);
    r.getCell(1).font = { name: 'Arial', size: 10, color: { argb: '475569' } };
    r.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    currentRowIdx++;
  };

  if (meta.clientAddress) {
    addMetaRow(String(sanitizeCell(meta.clientAddress)));
  } else if (meta.clientName) {
    addMetaRow(String(sanitizeCell(meta.clientName)));
  }

  const contractorParts: string[] = [];
  if (meta.contractorName) contractorParts.push(`${t('excel.contractor', 'Исполнитель')}: ${meta.contractorName}`);
  if (meta.contractorPhone) contractorParts.push(`Tel: ${meta.contractorPhone}`);
  if (contractorParts.length > 0) {
    addMetaRow(contractorParts.join(' | '));
  }

  if (meta.contractNumber) {
    addMetaRow(`${t('excel.contract', 'Договор №')}: ${meta.contractNumber}`);
  }

  const dateStr = meta.date || new Date().toLocaleDateString();
  addMetaRow(`${t('excel.date', 'Дата составления')}: ${dateStr}`);

  // Blank spacer row
  worksheet.addRow([]);
  currentRowIdx++;

  // 3. Table Header
  const headerRow = worksheet.addRow([
    t('excel.col_num', 'Lp.'),
    t('excel.col_work', 'Zakres prac'),
    t('excel.col_unit', 'Jedn.'),
    t('excel.col_rate', 'Stawka', { currency: cur }),
    t('excel.col_qty', 'Ilość'),
    t('excel.col_total', 'Łącznie', { currency: cur }),
    t('excel.col_notes', 'Uwagi')
  ]);
  headerRow.height = 25;
  headerRow.eachCell((cell, colNumber) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: '1E293B' } // Slate-800
    };
    cell.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFF' } };
    cell.alignment = {
      vertical: 'middle',
      horizontal: colNumber === 2 ? 'left' : (colNumber === 4 || colNumber === 5 || colNumber === 6 ? 'right' : 'center')
    };
    cell.border = thinBorder;
  });
  currentRowIdx++;

  const noQtyNote = t('excel.note_no_qty', 'Do wyceny po pomiarach') as string;
  let itemNumber = 0;

  // 4. Rooms Content
  rooms.forEach((room) => {
    // WR-01: pass safetyMargin so qty column matches margin-adjusted totals
    const { items: calculatedItems } = calculateRoomTotal(room, safetyMargin);
    if (calculatedItems.length === 0) return;

    // Room Header Row
    const roomHeaderVal = t('excel.room_section', '── {{name}} ──', { name: room.name.toUpperCase() });
    const rHeaderRow = worksheet.addRow([roomHeaderVal]);
    rHeaderRow.height = 24;
    worksheet.mergeCells(`A${currentRowIdx}:G${currentRowIdx}`);
    
    const rCell = rHeaderRow.getCell(1);
    rCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'E2E8F0' } // Slate-200
    };
    rCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '0F172A' } };
    rCell.alignment = { vertical: 'middle', horizontal: 'center' };
    rCell.border = thinBorder;
    currentRowIdx++;

    // Grouping by Category
    const itemsByCategory = new Map<string, CalculatedEstimateItem[]>();
    const categoryOrder: string[] = [];

    calculatedItems.forEach((item) => {
      const catKey = item.categoryId || 'uncategorized';
      if (!itemsByCategory.has(catKey)) {
        itemsByCategory.set(catKey, []);
        categoryOrder.push(catKey);
      }
      itemsByCategory.get(catKey)!.push(item);
    });

    categoryOrder.forEach((catKey) => {
      const sampleItem = itemsByCategory.get(catKey)![0];
      const catName = sampleItem?.categoryName || sampleItem?.category || t('excel.uncategorized', 'Inne');

      // Category Header Row
      const catHeaderVal = t('excel.category_section', '── {{name}} ──', { name: catName });
      const cHeaderRow = worksheet.addRow([catHeaderVal]);
      cHeaderRow.height = 20;
      worksheet.mergeCells(`A${currentRowIdx}:G${currentRowIdx}`);

      const cCell = cHeaderRow.getCell(1);
      cCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F1F5F9' } // Slate-100
      };
      cCell.font = { name: 'Arial', size: 10, bold: true, italic: true, color: { argb: '334155' } };
      cCell.alignment = { vertical: 'middle', horizontal: 'left' };
      cCell.border = thinBorder;
      currentRowIdx++;

      // Items Rows
      const catItems = itemsByCategory.get(catKey)!;
      catItems.forEach((item, itemIdx) => {
        itemNumber++;
        const qty = item.qty;
        const hasQty = qty > 0;
        const notes: string[] = [];

        if (!hasQty) {
          notes.push(noQtyNote);
        }
        // WR-02: use extracted helper to avoid duplicated ternary chains
        const adjDisplay = formatAdjDisplay(item.adjustment);
        if (adjDisplay) notes.push(adjDisplay);
        const adjSuffix = adjDisplay ? ` (${adjDisplay})` : '';
        const itemName = item.name + adjSuffix;
        const unitLabel = formatUnit(item.unitType || item.unit);
        const notesStr = notes.length > 0 ? notes.join('; ') : '';

        const itemRow = worksheet.addRow([
          itemNumber,
          sanitizeCell(itemName),
          unitLabel,
          item.priceLabor,
          hasQty ? qty : null,
          null, // Will set pre-computed lineTotal below
          notesStr || null
        ]);
        itemRow.height = 20;

        // Apply Zebra Stripes and formatting
        const isEven = itemIdx % 2 === 0;
        const rowColor = isEven ? 'FFFFFF' : 'F8FAFC';

        itemRow.eachCell((cell, colNum) => {
          cell.border = thinBorder;
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: rowColor }
          };
          cell.font = { name: 'Arial', size: 9, color: { argb: '334155' } };
          
          if (colNum === 1 || colNum === 3) {
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
          } else if (colNum === 2) {
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          } else if (colNum === 7) {
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
          } else {
            cell.alignment = { vertical: 'middle', horizontal: 'right' };
          }
        });

        // Numeric and Currency Formatting
        const numFmt = `#,##0.00" ${cur}"`;
        itemRow.getCell(4).numFmt = numFmt; // Rate
        itemRow.getCell(5).numFmt = `#,##0.00`; // Qty

        // Pre-computed line total from computeEstimateTotals (PDF-03: replaces D*E formula).
        // lineResult.lineTotal = roundTo(finalQty * priceLabor, 2) — already rounded.
        // This eliminates the formula-vs-roundTo divergence documented in RESEARCH.md.
        const totalCell = itemRow.getCell(6);
        totalCell.numFmt = numFmt;
        if (hasQty) {
          const lineResult = totalsResult.lines.find((l) => l.itemId === item.id);
          totalCell.value = lineResult?.lineTotal ?? roundTo(qty * item.priceLabor, 2);
        } else {
          totalCell.value = 0;
        }

        currentRowIdx++;
      });
    });

    // Space after room
    worksheet.addRow([]);
    currentRowIdx++;
  });

  // 5. Grand Total Row
  const totalRow = worksheet.addRow([
    '',
    '',
    '',
    t('excel.total_estimate', 'ВСЕГО ПО СМЕТЕ:'),
    '',
    null,
    ''
  ]);
  totalRow.height = 28;
  worksheet.mergeCells(`A${currentRowIdx}:E${currentRowIdx}`);

  // Styles for Grand Total
  totalRow.eachCell((cell) => {
    cell.border = {
      top: { style: 'thin', color: { argb: '10B981' } },
      bottom: { style: 'double', color: { argb: '047857' } }
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D1FAE5' } // Emerald-100
    };
  });

  // Label cell
  const labelCell = totalRow.getCell(1);
  labelCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '065F46' } };
  labelCell.alignment = { vertical: 'middle', horizontal: 'right' };

  // Value cell
  const valueCell = totalRow.getCell(6);
  valueCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '065F46' } };
  valueCell.alignment = { vertical: 'middle', horizontal: 'right' };
  valueCell.numFmt = `#,##0.00" ${cur}"`;

  // Pre-computed netTotal from computeEstimateTotals (PDF-03: replaces SUM formula).
  // Uses the same value the calculator and PDF display — guarantees cross-format parity.
  valueCell.value = totalsResult.netTotal;
  currentRowIdx++;

  // Spacing before footnotes
  worksheet.addRow([]);
  currentRowIdx++;

  // 6. Footnotes
  const addFootnote = (text: string) => {
    const fRow = worksheet.addRow([text]);
    fRow.height = 16;
    worksheet.mergeCells(`A${currentRowIdx}:G${currentRowIdx}`);
    const cell = fRow.getCell(1);
    cell.font = { name: 'Arial', size: 8.5, italic: true, color: { argb: '64748B' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    currentRowIdx++;
  };

  addFootnote(t('excel.footnote_final', 'Ostateczna cena po końcowym uzgodnieniu pełnego zakresu prac.') as string);
  addFootnote(t('excel.footnote_payment', 'Płatność częściami po zakończeniu poszczególnych etapów prac.') as string);
  addFootnote(t('excel.footnote_settlement', 'Ostateczne rozliczenie po odbiorze wykonanych prac i materiałów.') as string);

  // MAT-04: Materials section — only when materialTotals provided and non-empty.
  // Absent param ⇒ no materials section (labour-only export unchanged).
  // Pre-computed values rule: write line.lineTotal directly — NEVER =qty*price formula.
  if (materialTotals && materialTotals.lines.length > 0) {
    // Blank spacer before materials
    worksheet.addRow([]);
    currentRowIdx++;

    // Materials section header
    const matHeader = worksheet.addRow([t('materials.excel_section', 'Materiały') as string]);
    matHeader.height = 26;
    worksheet.mergeCells(`A${currentRowIdx}:G${currentRowIdx}`);
    const matHeaderCell = matHeader.getCell(1);
    matHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '92400E' } };
    matHeaderCell.font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FFFFFF' } };
    matHeaderCell.alignment = { vertical: 'middle', horizontal: 'center' };
    matHeaderCell.border = thinBorder;
    currentRowIdx++;

    // Materials column header row
    const matColHeader = worksheet.addRow([
      t('materials.col_material', 'Material'),
      t('materials.col_surface', 'Surface'),
      t('materials.col_norm', 'Norm'),
      t('materials.col_qty', 'Qty'),
      t('materials.col_unit', 'Unit'),
      t('materials.col_price', 'Price'),
      t('materials.col_total', 'Total'),
    ]);
    matColHeader.height = 22;
    matColHeader.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FEF3C7' } };
      cell.font = { name: 'Arial', size: 9, bold: true, color: { argb: '78350F' } };
      cell.border = thinBorder;
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
    currentRowIdx++;

    // Material lines — pre-computed values only (PDF-03 anti-pattern: no =qty*price formula)
    materialTotals.lines.forEach((line, idx) => {
      const matRow = worksheet.addRow([
        sanitizeCell(line.material.name),
        sanitizeCell(line.surfaceType),
        roundTo(line.norm, 3),
        roundTo(line.quantity, 3),
        sanitizeCell(line.material.unit),
        roundTo(line.pricePerUnit, 2),
        line.lineTotal, // pre-computed by buildBOM to 2dp — display as-is, never re-round
      ]);
      matRow.height = 18;
      const isEven = idx % 2 === 0;
      const rowColor = isEven ? 'FFFBEB' : 'FFFFFF';
      matRow.eachCell((cell, colNum) => {
        cell.border = thinBorder;
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowColor } };
        cell.font = { name: 'Arial', size: 9, color: { argb: '78350F' } };
        if (colNum >= 3 && colNum <= 7) {
          cell.numFmt = '#,##0.000';
          cell.alignment = { vertical: 'middle', horizontal: 'right' };
        } else {
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
        }
      });
      // Currency format for price and total columns (6 and 7)
      matRow.getCell(6).numFmt = `#,##0.00" ${cur}"`;
      matRow.getCell(7).numFmt = `#,##0.00" ${cur}"`;
      currentRowIdx++;
    });

    // Materials net total row
    const matTotalRow = worksheet.addRow([
      '',
      '',
      '',
      t('materials.net_total', 'Materials net total:'),
      '',
      '',
      materialTotals.netTotal, // sum of already-rounded lineTotals — display as-is
    ]);
    matTotalRow.height = 22;
    worksheet.mergeCells(`A${currentRowIdx}:F${currentRowIdx}`);
    matTotalRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FDE68A' } };
      cell.border = { top: { style: 'thin', color: { argb: '92400E' } }, bottom: { style: 'double', color: { argb: '92400E' } } };
    });
    const matLabelCell = matTotalRow.getCell(1);
    matLabelCell.font = { name: 'Arial', size: 10, bold: true, color: { argb: '78350F' } };
    matLabelCell.alignment = { vertical: 'middle', horizontal: 'right' };
    const matValueCell = matTotalRow.getCell(7);
    matValueCell.font = { name: 'Arial', size: 11, bold: true, color: { argb: '78350F' } };
    matValueCell.numFmt = `#,##0.00" ${cur}"`;
    matValueCell.alignment = { vertical: 'middle', horizontal: 'right' };
    currentRowIdx++;
  }

  // Generate buffer and save/share
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const fileName = `kosztorys_${new Date().toISOString().slice(0, 10)}.xlsx`;
  const file = new File([blob], fileName, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const nav = navigator as Navigator & {
    canShare?: (data?: { files?: File[] }) => boolean;
  };

  if (navigator.share && nav.canShare && nav.canShare({ files: [file] })) {
    try {
      await navigator.share({
        files: [file],
        title: t('excel.share_title', 'Kosztorys') as string,
        // TRUTH-05: share text total must agree with the attached workbook's grand total —
        // both read totalsResult.netTotal, the same value written to the total cell above.
        text: t('excel.share_text', 'Kosztorys na kwotę {{total}} {{currency}}', {
          total: roundTo(totalsResult.netTotal, 0),
          currency: cur,
        }) as string,
      });
      return true;
    } catch (error: unknown) {
      const err = error as Error;
      if (err.name !== 'AbortError') {
        console.error('Share error:', error);
        downloadFile(blob, fileName);
      }
    }
  } else {
    downloadFile(blob, fileName);
  }
};

const downloadFile = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoke object URL after click to prevent memory leak
  URL.revokeObjectURL(url);
};